/**
 * axios 单实例 —— 09 章 §9.13。
 *
 * 这一层承担四件别处不该重复做的事：
 *   1. 挂 `Authorization`，并在 401 时**单飞刷新 + 请求排队**（§9.13.4）
 *   2. 把后端的 `{code, message, data, detail}` 错误信封变成一个带 `code` 的 `ApiError`
 *   3. 幂等提交的 `X-Client-Token`（§9.13.5）
 *   4. 后端版本变化探测（§9.13.6）
 *
 * **不做的事**：不弹 toast。反馈的位置纪律在 §9.11.1——
 * 表单错误进表单、列表错误进四态渲染、只有"用户主动触发且没有承载位置"的
 * 才用 toast。拦截器不知道调用方有没有承载位置，所以它只抛，由调用方决定怎么显示。
 */

import { ref } from 'vue'

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

import type { ErrorCode, ErrorEnvelope, SuccessEnvelope, TokenPair } from '@/types/api'

// ── 存储键。集中在这里，登出清理时不会漏 ──────────────────────

export const STORAGE_ACCESS = 'mtp.access_token'
export const STORAGE_REFRESH = 'mtp.refresh_token'
/** 跨标签同步用（§9.10.4）。写它触发别的标签页的 `storage` 事件。 */
export const STORAGE_AUTH_EPOCH = 'mtp.auth_epoch'

export const API_BASE = '/api/v1'

// ── 错误 ──────────────────────────────────────────────────────

/**
 * Pydantic 往字段消息前面加的那截框架黑话，剥掉。
 *
 * 判据是实测的（2026-08-04，真浏览器）：模型端点的地址填成 `…:7788/v1` 时，
 * 页面上显示的是 **「Value error, 填服务根地址即可，不要带 /v1（改成 …）」**。
 * 逗号后面那半句是我们自己写的、用户能照着改的话；前面那半句对他毫无意义，
 * 而且会让整条提示看起来像一个程序错误而不是一条填写指引。
 *
 * 剥在这里而不是各个页面：`fieldErrors` 是全站唯一的出口，
 * 而**没有任何一个页面**会想显示这个前缀。落在页面上就等于每个表单各记一次。
 *
 * 只剥已知的两个前缀，不做通用的「逗号前都算噪声」—— 后者会把
 * 「名称不能为空，请填写」这种正常句子拦腰砍掉。
 */
function stripPydanticPrefix(message: string): string {
  for (const noise of ['Value error, ', 'Assertion failed, ']) {
    if (message.startsWith(noise)) return message.slice(noise.length)
  }
  return message
}

/**
 * 统一的错误对象。**调用方一律 catch 这个**，不要去摸 `AxiosError`。
 *
 * `fieldErrors` 只在 422 时有值，形状见 §9.13.3：后端的
 * `ValidationFailed.detail` 里带 `fields: {name: message}`。
 */
export class ApiError extends Error {
  readonly code: ErrorCode | 'NETWORK' | 'TIMEOUT' | 'CANCELED' | 'UNKNOWN'
  readonly httpStatus: number | undefined
  readonly detail: Record<string, unknown>

  constructor(
    code: ApiError['code'],
    message: string,
    httpStatus?: number,
    detail: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.httpStatus = httpStatus
    this.detail = detail
  }

  /**
   * 422 的字段级错误 —— `{ learning_rate: '必须大于 0' }`。
   *
   * 后端在这条链上发**三种**形状，只认一种的话表单页的红字永远挂不上：
   *
   * | 出处 | 形状 |
   * |---|---|
   * | 少数端点 | `detail.fields` 是 `{字段: 消息}` 字典 |
   * | `job_submit._fail(...)` | `detail.field` —— **单数、字符串** |
   * | `assert_param_spec_ok` / `assert_no_managed_keys` | `detail.fields` 是**数组** |
   *
   * 只认字典的后果实测过：`TrainCreateView` / `QuantizeCreateView` 里那句
   * `fieldErrors.value = { ...err.fieldErrors }` 恒得到空对象，用户只看到顶部
   * 一句 message，得自己在二十个输入框里猜是哪一个。
   *
   * 后两种没有逐字段消息，回落成信封的 `message`：让红字**出现在正确的位置**
   * 比让它说一句更精确的话重要 —— 位置本身就是最主要的信息。
   */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    const raw = this.detail['fields']

    // 单数 `field`：一个字段名 + 信封的 message。
    const single = this.detail['field']
    if (typeof single === 'string' && single) out[single] = this.message

    if (Array.isArray(raw)) {
      for (const name of raw) {
        if (typeof name === 'string' && name) out[name] = this.message
      }
    } else if (raw && typeof raw === 'object') {
      // 字典形态最具体，放最后写 —— 它要能盖掉上面两种的回落消息。
      for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
        const message = stripPydanticPrefix(String(value))
        out[key] = message
        // DTO 层的键是**点分路径**（`body.new_password`）：后端
        // `exception_handlers.py` 把 pydantic 的 `loc` 原样拼起来，而页面读的是裸
        // 字段名。只写全路径的后果是**静默失败** —— `errors['new_password']` 取不到，
        // 而 `Object.keys(fieldErrors).length` 又不为 0 ⇒ 连兜底那句都不显示，
        // 用户点了提交像没反应。两个键都写：全路径保留给按它读的调用方。
        const bare = key.slice(key.lastIndexOf('.') + 1)
        if (bare && !(bare in out)) out[bare] = message
      }
    }
    return out
  }

  /** 追查用。500 的信封里带 `detail.request_id`，日志里能按它找到那一次请求。 */
  get requestId(): string | undefined {
    const rid = this.detail['request_id']
    return typeof rid === 'string' ? rid : undefined
  }

  get isCanceled(): boolean {
    return this.code === 'CANCELED'
  }

  /** 用户是否可以"改一下再试"。用来决定是给重试按钮还是给"联系管理员"。 */
  get isRetryable(): boolean {
    return this.code === 'NETWORK' || this.code === 'TIMEOUT' || this.code === 'RATE_LIMITED'
  }
}


/**
 * 一个 `ApiError` → 一句给人看的话。表单顶部那条红字统一走它。
 *
 * Args:
 *   err: 任意 `ApiError`。
 *
 * Returns:
 *   逐字段消息按「；」连接；没有字段级消息时回落成信封的 `message`。
 *
 * **必须去重**：`fieldErrors` 是**按键查**的表，同一条消息被刻意写了两个键
 * （全路径 `body.base_url` 与裸名 `base_url`，见上面那段注释与
 * `client.spec.ts` 的两条判据）。按值枚举就把别名数了两遍 —— 页面上一句话出现
 * 两次，重复倍数 = 带点分路径的报错字段数 × 2。
 *
 * 收在这里而不是各页面各写一份：三处曾各抄一份 `explain()`，同一个 bug 就有三份
 * （2026-08-25 用户在模型端点对话框上看到「地址要以 http:// 开头」说了两遍）。
 */
export function explainApiError(err: ApiError): string {
  const said = [...new Set(Object.values(err.fieldErrors))]
  return said.length ? said.join('；') : err.message
}

/** 下载响应体的解包上限。错误信封只有几百字节，超过它的一定不是信封。 */
const BLOB_ERROR_MAX_BYTES = 64 * 1024

/**
 * 读 `Blob` 的文本。
 *
 * `Blob.prototype.text` 不是处处都有（jsdom 26 就没有实现），
 * `FileReader` 是更老也更通用的那条路。
 */
function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text()
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error ?? new Error('读取响应体失败'))
    reader.readAsText(blob)
  })
}

/**
 * `responseType: 'blob'` 的请求出错时，把错误响应体解回信封。
 *
 * axios 的 `responseType` 对**错误响应也生效**：下载端点 409 时 `res.data` 是一个
 * `Blob`，而它没有 `.code`，`toApiError` 只能落到「请求失败（HTTP 409）」那条兜底 ——
 * 后端写好的「原始文件已不在这台母机上。重新上传同名文件后可以再下载」一个字都到不了页面。
 *
 * 就地改写 `res.data`，让下游按普通 JSON 错误处理。不是 JSON 时留下文本，
 * 由 `toApiError` 的短文本分支兜。
 */
async function unpackBlobBody(res: AxiosResponse): Promise<void> {
  const body: unknown = res.data
  if (typeof Blob === 'undefined' || !(body instanceof Blob)) return
  if (body.size > BLOB_ERROR_MAX_BYTES) return
  let text: string
  try {
    text = await readBlobText(body)
  } catch {
    // 读不出来就维持原状，由 `toApiError` 走「请求失败（HTTP N）」那条兜底。
    return
  }
  try {
    res.data = JSON.parse(text) as unknown
  } catch {
    // 网关的 HTML 错误页之类：保留文本，短的会被当成 message 显示。
    res.data = text
  }
}

function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err

  if (axios.isCancel(err)) {
    return new ApiError('CANCELED', '请求已取消')
  }

  if (err instanceof AxiosError) {
    if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
      return new ApiError('TIMEOUT', '请求超时，请稍后重试')
    }
    const res = err.response
    if (!res) {
      return new ApiError('NETWORK', '无法连接到服务器，请检查网络或后端是否在运行')
    }

    const body = res.data as Partial<ErrorEnvelope> | string | undefined
    if (body && typeof body === 'object' && typeof body.code === 'string') {
      return new ApiError(
        body.code as ErrorCode,
        body.message || '请求失败',
        res.status,
        (body.detail as Record<string, unknown>) ?? {},
      )
    }

    // 没有信封的响应：网关、nginx、或 FastAPI 自己的 422。
    // FastAPI 原生 422 的形状是 `{detail: [{loc, msg, type}]}`——理论上后端
    // 已经全部包成信封了，但代理层插进来的错误页不会有信封，所以这里要兜。
    return new ApiError(
      res.status === 422 ? 'VALIDATION_FAILED' : 'UNKNOWN',
      typeof body === 'string' && body.length < 200 ? body : `请求失败（HTTP ${res.status}）`,
      res.status,
      typeof body === 'object' ? ((body as Record<string, unknown>) ?? {}) : {},
    )
  }

  return new ApiError('UNKNOWN', err instanceof Error ? err.message : String(err))
}

// ── token 存取 ────────────────────────────────────────────────

/**
 * token 存取。
 *
 * **两种存储是「记住登录」开关的唯一实现**：
 *   · 勾选 → `localStorage`，关掉浏览器再开仍然登录着；
 *   · 不勾 → `sessionStorage`，关掉标签页就失效。
 *
 * 读取时两处都查（先 session 后 local）：同一台机器上可能一个标签勾了、
 * 另一个没勾，只查一处会把另一种情况的人直接踢到登录页。
 *
 * `STORAGE_AUTH_EPOCH` 始终写在 `localStorage`：它是**跨标签广播**用的，
 *   sessionStorage 不跨标签，写在那里的话「一个标签登出、其它标签跟着退」就失效了。
 */
/**
 * storage 不是响应式来源。`auth.token` 是 `computed(() => tokenStore.access)`，
 * 没有这个版本号的话它在首次求值（未登录，null）后**永不重算** —— 登录写完
 * storage，守卫读到的 token 仍是缓存的 null，于是带 `?redirect=` 登录后
 * 被弹回登录页，而整页刷新（重建应用）又是好的。这个「第一次不行刷新就行」
 * 的形态 2026-08-04 实测过一次。set/clear 时递增，getter 里 touch 一下建立依赖。
 */
const tokenVersion = ref(0)

export const tokenStore = {
  get access(): string | null {
    void tokenVersion.value // 只为建立响应式依赖，storage 仍是唯一事实源
    return sessionStorage.getItem(STORAGE_ACCESS) ?? localStorage.getItem(STORAGE_ACCESS)
  },
  get refresh(): string | null {
    void tokenVersion.value
    return sessionStorage.getItem(STORAGE_REFRESH) ?? localStorage.getItem(STORAGE_REFRESH)
  },
  /** @param persist 记住登录（默认 true，与改造前的行为一致）。 */
  set(pair: TokenPair, persist = true): void {
    const target = persist ? localStorage : sessionStorage
    const other = persist ? sessionStorage : localStorage
    // 先清另一边：不清的话切换「记住」之后两边各存一份，
    // 登出只清一边就会出现「明明退了，刷新又进去了」。
    other.removeItem(STORAGE_ACCESS)
    other.removeItem(STORAGE_REFRESH)

    target.setItem(STORAGE_ACCESS, pair.access_token)
    target.setItem(STORAGE_REFRESH, pair.refresh_token)
    localStorage.setItem(STORAGE_AUTH_EPOCH, String(Date.now()))
    tokenVersion.value++
  },
  clear(): void {
    for (const s of [localStorage, sessionStorage]) {
      s.removeItem(STORAGE_ACCESS)
      s.removeItem(STORAGE_REFRESH)
    }
    localStorage.setItem(STORAGE_AUTH_EPOCH, String(Date.now()))
    tokenVersion.value++
  },
}

// ── 会话失效的广播 ────────────────────────────────────────────
//
// 拦截器不能 `import { useAuthStore }`——那会造成
// client → store → client 的循环依赖（§9.10.4 的依赖方向）。
// 所以用一个注册回调：main.ts 启动时把 store 的登出动作注册进来。

type SessionExpiredHandler = (reason: 'refresh_failed' | 'revoked') => void
let onSessionExpired: SessionExpiredHandler = () => {}

export function registerSessionExpiredHandler(fn: SessionExpiredHandler): void {
  onSessionExpired = fn
}

// ── 强制改密（后端 `core/deps.py::assert_password_changed`）──────
//
// 后端现在会把 `must_change_password=true` 的用户挡在**除自助那几条之外**的所有
// 端点外，回 403 + `detail.reason = "PASSWORD_CHANGE_REQUIRED"`。
// 路由守卫已经在拦，但它拦不住两种情况：
//   ① 标志位是在这个标签页打开**之后**被管理员置上的（守卫早就跑过了）
//   ② 组件里的轮询还在继续发请求
// 那两种情况下页面会满屏「没有权限」，而正确的动作是去改密。所以这里也接一次。

type PasswordChangeRequiredHandler = () => void
let onPasswordChangeRequired: PasswordChangeRequiredHandler = () => {}

export function registerPasswordChangeRequiredHandler(fn: PasswordChangeRequiredHandler): void {
  onPasswordChangeRequired = fn
}

// ── 后端版本变化探测（§9.13.6）────────────────────────────────

type VersionChangeHandler = (from: string, to: string) => void
let onVersionChange: VersionChangeHandler = () => {}
let seenVersion: string | null = null

export function registerVersionChangeHandler(fn: VersionChangeHandler): void {
  onVersionChange = fn
}

// ── 实例 ──────────────────────────────────────────────────────

export const http: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 30_000,
  // ⚠️ **不设全局 `Content-Type`。**
  //
  //   写死 `application/json` 会毁掉 multipart 上传：`FormData` 的请求头
  //   必须是 `multipart/form-data; boundary=----WebKitFormBoundary...`，
  //   而那个 boundary 只有浏览器自己知道。头被写死之后服务端解析出一个
  //   **空表单**，症状是请求挂在那里不返回（FastAPI 等一个永远读不完的体），
  //   页面上的按钮永远转圈 —— 完全看不出是请求头的问题。
  //
  //   axios 会按 body 的类型自动选：普通对象 → JSON，`FormData` → multipart。
  //   这正是它该做的事。
  // 后端所有错误码都在信封里，让 axios 一律走 reject 分支，
  // 由 `toApiError` 统一翻译。不要在这里 `validateStatus: () => true`
  // ——那会让每个调用点都要自己判断成功失败。
})

/** 需要幂等保护的方法。GET/HEAD 天然幂等，不需要 token。 */
const IDEMPOTENT_METHODS = new Set(['post', 'put', 'patch'])

/** 幂等 token 的注入口。表单的 `useIdempotentSubmit` 会把它塞进 config。 */
export interface MtpRequestConfig extends AxiosRequestConfig {
  /** 幂等键。同一个键重复提交，后端返回第一次的结果而不是再建一个 job。 */
  clientToken?: string
  /** 跳过 401 自动刷新。登录/刷新自身必须置 true，否则会自己套自己。 */
  skipAuthRefresh?: boolean
}

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.access
  if (token && !config.headers.has('Authorization')) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }

  const cfg = config as InternalAxiosRequestConfig & MtpRequestConfig
  const method = (config.method ?? 'get').toLowerCase()
  if (cfg.clientToken && IDEMPOTENT_METHODS.has(method)) {
    config.headers.set('X-Client-Token', cfg.clientToken)
  }

  return config
})

// ── 401 的单飞刷新（§9.13.4）──────────────────────────────────
//
// 为什么必须单飞：一个页面同时发 5 个请求，token 刚过期 → 5 个 401 →
// 若各自去刷新，后 4 个用的 refresh_token 已被第 1 个用掉。而后端的
// refresh 是**一次性**的（M1 实测：重置口令后旧 refresh 立刻 401，
// 且有 `auth.refresh_reuse_detected` 审计动作），复用会被判定为盗用，
// 直接吊销整个会话——用户被莫名踢下线。

let refreshInflight: Promise<TokenPair> | null = null

async function refreshTokens(): Promise<TokenPair> {
  const refresh = tokenStore.refresh
  if (!refresh) throw new ApiError('UNAUTHENTICATED', '没有可用的刷新令牌')

  // 用裸 axios 而不是 `http`，避免请求拦截器再挂上那张过期的 access token
  const res = await axios.post<SuccessEnvelope<TokenPair> | TokenPair>(
    `${API_BASE}/auth/refresh`,
    { refresh_token: refresh },
    { timeout: 15_000 },
  )
  const pair = unwrap(res.data)
  tokenStore.set(pair)
  return pair
}

function ensureRefresh(): Promise<TokenPair> {
  if (!refreshInflight) {
    refreshInflight = refreshTokens().finally(() => {
      refreshInflight = null
    })
  }
  return refreshInflight
}

http.interceptors.response.use(
  (res) => {
    // 后端版本变化 → 提示用户刷新页面（§9.13.6）。
    // 这条是防"前端还是旧的、后端已经换了接口形状"这类幽灵故障。
    const version = res.headers['x-mtp-version']
    if (typeof version === 'string' && version) {
      if (seenVersion && seenVersion !== version) {
        onVersionChange(seenVersion, version)
      }
      seenVersion = version
    }
    return res
  },
  async (error: unknown) => {
    if (!(error instanceof AxiosError) || !error.response || !error.config) {
      throw toApiError(error)
    }

    const cfg = error.config as InternalAxiosRequestConfig &
      MtpRequestConfig & { _retried?: boolean }

    // 下载类请求（`responseType: 'blob'`）的错误体先解回信封，
    // 否则后面所有分支拿到的都是一个没有 `code` 的 Blob。
    await unpackBlobBody(error.response)

    const shouldRefresh =
      error.response.status === 401 &&
      !cfg.skipAuthRefresh &&
      !cfg._retried &&
      Boolean(tokenStore.refresh) &&
      // 刷新端点自己 401 时绝不再刷（否则无限递归）
      !cfg.url?.includes('/auth/refresh') &&
      !cfg.url?.includes('/auth/login')

    if (shouldRefresh) {
      cfg._retried = true
      try {
        const pair = await ensureRefresh()
        cfg.headers.set('Authorization', `Bearer ${pair.access_token}`)
        return await http.request(cfg)
      } catch {
        tokenStore.clear()
        onSessionExpired('refresh_failed')
        throw new ApiError('UNAUTHENTICATED', '登录已过期，请重新登录', 401)
      }
    }

    if (error.response.status === 403) {
      const detail = (error.response.data as { detail?: { reason?: unknown } } | undefined)?.detail
      if (detail?.reason === 'PASSWORD_CHANGE_REQUIRED') onPasswordChangeRequired()
    }

    if (error.response.status === 401 && !cfg.url?.includes('/auth/')) {
      // 没有 refresh 可用，或已经重试过一次还是 401（口令被重置、token_version 变了）
      tokenStore.clear()
      onSessionExpired('revoked')
    }

    throw toApiError(error)
  },
)

// ── 便捷包装。**返回 data，不返回 AxiosResponse** ──────────────
//
// 这层薄包装的意义：让 api/*.ts 里全是 `return get<Page<Job>>('/jobs', params)`
// 这样的一行，而不是每处都 `.then(r => r.data)`——漏一个就是个
// "拿到了整个 AxiosResponse 当业务对象用" 的 bug。

/** `params` 收 `object` 而不是 `Record<string, unknown>`：后者要求索引签名，
 *  而 `interface PageQuery { page?: number }` 这类具名接口没有索引签名，
 *  每个调用点都得 `as Record<...>` 转一次——转多了就会有人顺手 `as any`。 */
function unwrap<T>(response: SuccessEnvelope<T> | T): T {
  // 滚动发布期间，浏览器先拿到新前端、后端进程尚未重启是常态。兼容裸响应
  // 只是一段过渡保护；新后端的普通 JSON 一律走 SUCCESS 信封。
  if (
    response !== null &&
    typeof response === 'object' &&
    (response as Partial<SuccessEnvelope<T>>).code === 'SUCCESS' &&
    'data' in response
  ) {
    return (response as SuccessEnvelope<T>).data
  }
  return response as T
}

export async function get<T>(url: string, params?: object, config?: MtpRequestConfig): Promise<T> {
  const res = await http.get<SuccessEnvelope<T> | T>(url, { ...config, params })
  return unwrap(res.data)
}

export async function post<T>(url: string, body?: unknown, config?: MtpRequestConfig): Promise<T> {
  const res = await http.post<SuccessEnvelope<T> | T>(url, body, config)
  return unwrap(res.data)
}

export async function patch<T>(url: string, body?: unknown, config?: MtpRequestConfig): Promise<T> {
  const res = await http.patch<SuccessEnvelope<T> | T>(url, body, config)
  return unwrap(res.data)
}

export async function put<T>(url: string, body?: unknown, config?: MtpRequestConfig): Promise<T> {
  const res = await http.put<SuccessEnvelope<T> | T>(url, body, config)
  return unwrap(res.data)
}

export async function del<T = void>(url: string, config?: MtpRequestConfig): Promise<T> {
  const res = await http.delete<SuccessEnvelope<T> | T>(url, config)
  return res.status === 204 ? (undefined as T) : unwrap(res.data)
}

/**
 * 生成幂等键（UUID v4 形状）。
 *
 * **不能直接用 `crypto.randomUUID()`**：它只在安全上下文（https / localhost）存在。
 * 平台支持内网明文 http 访问（NET-HTTP-PLAIN，2026-08-31 起），`http://10.10.0.2`
 * 下它是 undefined —— 2026-09-02 线上实撞：所有任务看板 setup 阶段
 * `TypeError: crypto.randomUUID is not a function` 整页白屏，而本地 dev
 * （localhost = 安全上下文）完全正常，最难在开发时发现的一类分叉。
 *
 * `crypto.getRandomValues` 不受安全上下文限制，用它手拼 v4。
 */
export function newClientToken(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const b = new Uint8Array(16)
  crypto.getRandomValues(b)
  b[6] = (b[6]! & 0x0f) | 0x40 // version 4
  b[8] = (b[8]! & 0x3f) | 0x80 // variant 10xx
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
